import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetPatientReportsByClinicIdQuery } from "../State/Api/GetallappointmentsApiSlice";
import { useUploadLabReportMutation } from "../State/Api/GetallappointmentsApiSlice";
import { useFormik } from "formik";
import * as Yup from 'yup';
import {
  ArrowLeft,
  Trash2,
  FileText,
  Upload,
  Image,
  User,
  UserCheck,
  ClipboardList,
  ChevronDown,
  Search,
  X,
  Droplets, Heart, Weight, Ruler, Thermometer
} from "lucide-react";
import { toast, ToastContainer } from "react-toastify";
import { DatePicker } from "rsuite";
import FilePreviewSwitcher from "./FilePreviewSwitcher";
import "react-toastify/dist/ReactToastify.css";

const AddPatientReports = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const {data : patients, isLoading, isFetching, isSuccess} = useGetPatientReportsByClinicIdQuery(localStorage.getItem("userClinicID"));

  const [uploadReport] = useUploadLabReportMutation();

  const [selectedPatient, setSelectedPatient] = useState(patients?.[0]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [otherReports, setOtherReport] = useState(false);

   const dropdownRef = useRef(null);

  // ✅ Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);


  useEffect(() => {
  if (!selectedPatient && patients?.length > 0) {
    setSelectedPatient(patients[0]);
  }
}, [patients, selectedPatient]);

  const filteredPatients = patients?.filter(
    (patient) =>
      patient.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      patient.contactNumber.includes(searchTerm)
  );


  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    setIsDropdownOpen(false);
    setSearchTerm("");
  };



   
   const [prescribedTestList, setPrescribedTestList] = useState(selectedPatient?.testRecommendations?.map((test) => {
    return {
      testName : test.testName,
      reportDate : "",
      reportType : "",
      observationNote : "",
      imageupload: []
    }

   }) || []);
   const [selectedTest, setSelectedTest] = useState(0);

  // Handle selected test change (index)
  const handleTestChange = (e) => {
    const index = parseInt(e.target.value, 10);
    if (
      !isNaN(index) &&
      index >= 0 &&
      index < formik.values.uploadfiles.length
    ) {
      setSelectedTest(index);
      // Optional: reset editor content or other state as needed
      if (editorRef.current) {
        editorRef.current.innerHTML =
          formik.values.uploadfiles[index].observationNote || "";
      }
    }
  };

   useEffect(()=>{
    if(selectedPatient){
      setPrescribedTestList(selectedPatient?.testRecommendations?.map((test) => {
        return {
          testName : test.testName,
          reportDate : "",
          reportType : "",
          observationNote : "",
          imageupload: []
        }
    
       }) || []);
    }
   },[selectedPatient])
   

  const [activeCommands, setActiveCommands] = useState({});
  const editorRef = useRef(null);

  const formatText = (cmd) => {
    document.execCommand(cmd, false, null);
    updateActiveCommands();
  };

  const updateActiveCommands = () => {
    const commands = [
      "bold",
      "italic",
      "underline",
      "justifyLeft",
      "justifyCenter",
      "justifyRight",
    ];
    const active = {};
    commands.forEach((cmd) => {
      active[cmd] = document.queryCommandState(cmd);
    });
    setActiveCommands(active);
  };


  const toolbarButtons = [
    { cmd: "bold", label: <span className="font-bold">B</span> },
    { cmd: "italic", label: <span className="italic">I</span> },
    { cmd: "underline", label: <span className="underline">U</span> },
    { cmd: "justifyLeft", label: "⯇" },
    { cmd: "justifyCenter", label: "≡" },
    { cmd: "justifyRight", label: "⯈" },
    { cmd: "undo", label: "↺" },
    { cmd: "redo", label: "↻" },
  ];

  const [savedTests, setSavedTests] = useState([]);

const validationSchema = Yup.object({
  uploadfiles: Yup.array().of(
    Yup.object({
      testName: Yup.string().required('Test Name is required'),
      reportDate: Yup.string().required('Report Date is required'),
      reportType: Yup.string().required('Report Type is required'),
      observationNote: Yup.string().required('Observation Note is required'),
      imageupload: Yup.array().min(1, "Please add the relevant file, save and then Submit the report.")
    })
  ).min(1, 'You must save atleast one report befor submitting.')
})

const formik = useFormik({
  initialValues: {
    PatientUserUniqueID: selectedPatient?.userUniqueID || "",
    DoctorUserUniqueID: selectedPatient?.doctorUserUniqueID || "",
    UserClinicID: localStorage.getItem("userClinicID") || "",
    IsActive: true,
    UpdatedBy: "string",
    AppointmentID: selectedPatient?.appointmentID || 0,
    uploadfiles: [...prescribedTestList]
  },
  // Disable automatic validation
  validateOnChange: false,
  validateOnBlur: false,
  onSubmit: async (values, { setSubmitting }) => {
    // Your existing submission logic here
    const formdata = new FormData();
    const selectedTestData = values.uploadfiles[selectedTest];
    
    formdata.append("PatientUserUniqueID", values.PatientUserUniqueID);
    formdata.append("DoctorUserUniqueID", values.DoctorUserUniqueID);
    formdata.append("UserClinicID", values.UserClinicID);
    formdata.append("IsActive", values.IsActive);
    formdata.append("UpdatedBy", values.UpdatedBy);
    formdata.append("AppointmentID", values.AppointmentID);

    if(savedTests.length > 0){
      savedTests.map((test, index) => {
        formdata.append(`uploadfiles[${index}].testName`, test.testName);
        formdata.append(`uploadfiles[${index}].reportDate`, test.reportDate);
        formdata.append(`uploadfiles[${index}].reportType`, test.reportType);
        formdata.append(`uploadfiles[${index}].observationNote`, test.observationNote);
        test.imageupload.forEach((image, idx) => {
          formdata.append(`uploadfiles[${index}].imageupload[${idx}].formFile`, image.formFile);
          formdata.append(`uploadfiles[${index}].imageupload[${idx}].reportName`, image.reportName);
        });
      })
    } else {
      formdata.append('uploadfiles', JSON.stringify([]));
    }

    try {
      const result = await uploadReport(formdata).unwrap();
      if (result?.result_Code === 100) {
        toast.success("Submitted successfully", { autoClose: 3000 });
        setTimeout(() => {
          navigate("/PatientReports");
        }, 2000);
      } else if (result?.result_Code === 200) {
        toast.success("Submitted successfully", { autoClose: 3000 });
        setTimeout(() => {
          navigate("/PatientReports");
        }, 2000);
      } else {
        toast.warning("Please Save the report before submitting");
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setSubmitting(false);
    }
  },
  enableReinitialize: true
});

// Custom submit handler that validates first
const handleFormSubmit = async (e) => {
  e.preventDefault();
  
  try {
    console.log(formik.values);
    // Manually trigger validation
    await validationSchema.validate({...formik.values, uploadfiles: [...savedTests]}, { abortEarly: false });
    
    // If validation passes, proceed with form submission
    formik.handleSubmit(e);
  } catch (validationErrors) {
    // Show validation errors as toasts
    if (validationErrors.inner && validationErrors.inner.length > 0) {
      validationErrors.inner.forEach(error => {
        toast.error(error.message);
      });
    } else {
      toast.error(validationErrors.message);
    }
    
    // Set form errors and touched state to show field-level errors too
    formik.setErrors(validationErrors.inner.reduce((acc, error) => {
      acc[error.path] = error.message;
      return acc;
    }, {}));
    
    // Touch all fields to show errors
    formik.setTouched(
      validationErrors.inner.reduce((acc, error) => {
        acc[error.path] = true;
        return acc;
      }, {})
    );
  }
};


    const addReportFile = () => {
      const preserveValues = [...formik.values.uploadfiles[selectedTest]?.imageupload, { formFile: null, reportName: "", fileName:  "", fileUrl: "", fileType : "" }];
    formik.setFieldValue(`uploadfiles[${selectedTest}].imageupload`, preserveValues);
  };

  const handleFileChange = (e, fileIndex) => {
    const file = e.target.files[0];
   
    if (!file) return;
    formik.setFieldValue(
      `uploadfiles[${selectedTest}].imageupload[${fileIndex}].formFile`,
      file
    );
    formik.setFieldValue(
      `uploadfiles[${selectedTest}].imageupload[${fileIndex}].fileName`, file.name
    )
    formik.setFieldValue(
      `uploadfiles[${selectedTest}].imageupload[${fileIndex}].fileUrl`, URL.createObjectURL(file)
    )
    formik.setFieldValue(
      `uploadfiles[${selectedTest}].imageupload[${fileIndex}].fileType`, file.type.split("/")[1]
    )
  };

  const [saving, setSaving] = useState(false);
  
const handleSaveTest = () => {
  setSaving(true);
  const testToSave = formik.values.uploadfiles[selectedTest];

  setSavedTests((prevTests) => {
    const updatedTests = [...prevTests];
    const existingIndex = updatedTests.findIndex(
      (savedTest) => savedTest.testName === testToSave.testName
    );

    if (existingIndex !== -1) {
      updatedTests[existingIndex] = testToSave;
    } else {
      updatedTests.push(testToSave);
    }

    return updatedTests;
  });

  setTimeout(() => {
    setSaving(false); // Simulate async save delay
  }, 800); // Optional: simulate delay for UX
};

  const [previewFile, setPreviewFile] = useState(null);
  const [type, setFileType] = useState(null);
  
    const closePreview = () => setPreviewFile(null);
  
  useEffect(()=>{
    const getFileType = (filename, fileType) => {
    if(filename && ["jpg", "jpeg", "png", "webp", "gif", "bmp","pdf"].includes(filename.split(".").pop().toLowerCase())){
      const ext = filename.split(".").pop().toLowerCase();
     
    if (ext === "pdf") return { type: "pdf", label: "PDF", bg: "bg-red-100", text: "text-red-600" };
    if (["jpg", "jpeg", "png", "webp", "gif", "bmp"].includes(ext))
      return { type: "image", label: "IMG", bg: "bg-blue-100", text: "text-blue-600" };
    }
    else if(fileType !== "") {
      if (fileType === "pdf") return { type: "pdf", label: "PDF", bg: "bg-red-100", text: "text-red-600" };
    if (["jpg", "jpeg", "png", "webp", "gif", "bmp"].includes(fileType))
      return { type: "image", label: "IMG", bg: "bg-blue-100", text: "text-blue-600" };
    }
    return { type: "other", label: "FILE", bg: "bg-gray-200", text: "text-gray-600" };
  };
  
    if(previewFile){
      const fileType = getFileType(previewFile.fileUrl, previewFile.fileType);
      setFileType(fileType);
    }
  }, [previewFile]);

  let content = null;

  if(isLoading || isFetching) content = <div>Loading...</div>;

  if(isSuccess){
     content = <form onSubmit={handleFormSubmit} className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <style>{`
      
        .card-modern {
          background: white;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          border: 1px solid rgba(229, 231, 235, 0.6);
          transition: all 0.3s ease;
        }
        
        .card-modern:hover {
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
          transform: translateY(-2px);
        }
        
        .vitals-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 16px;
        }
        
        .vital-card {
          background: white;
          border-radius: 12px;
          padding: 20px;
          border: 1px solid rgba(229, 231, 235, 0.6);
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }
        
        .vital-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #10b981, #059669);
        }
        
        .vital-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
        }
        
        .rich-editor {
          min-height: 200px;
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          padding: 16px;
          background-color: white;
          transition: border-color 0.3s ease;
          outline: none;
        }
        
        .rich-editor:focus {
          border-color: #10b981;
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
        }
        
        .file-upload-area {
          border: 2px dashed #d1d5db;
          border-radius: 12px;
          padding: 24px;
          text-align: center;
          transition: all 0.3s ease;
          background: #fafafa;
          cursor: pointer;
        }
        
        .file-upload-area:hover {
          border-color: #10b981;
          background: #f0fdf4;
        }
        
        .file-item {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 16px;
          transition: all 0.3s ease;
        }
        
        .file-item:hover {
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
        }
        
        .test-selector {
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          padding: 12px 16px;
          transition: all 0.3s ease;
          font-weight: 500;
          width: 100%;
        }
        
        .test-selector:focus {
          outline: none;
          border-color: #10b981;
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
        }
        
        .priority-badge {
          display: inline-flex;
          align-items: center;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .priority-high {
          background: #fee2e2;
          color: #dc2626;
        }
        
        .priority-medium {
          background: #fef3c7;
          color: #d97706;
        }
        
        .priority-low {
          background: #dcfce7;
          color: #16a34a;
        }
        
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 20px;
        }
        
        .modal-content {
          background: white;
          border-radius: 16px;
          max-width: 90vw;
          max-height: 90vh;
          overflow: auto;
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
        }
        
        .modal-content img,
        .modal-content embed {
          max-width: 100%;
          max-height: 100%;
        }
          .rs-input-group{
  border-radius: 8px;
  height: 38px;
  border-color:1px solid #e5e5ea;
  }
      `}</style>

      <div className="max-w-9xl mx-auto">
        <ToastContainer position="top-right" />
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => navigate("/PatientReports")}
              className="flex items-center justify-center w-12 h-12 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                Add Patient Reports
              </h1>
              <p className="text-gray-600">
                Add patient reports and observations with detailed
                documentation.
              </p>
            </div>
          </div>
          <div className="h-px bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-2">
          {/* Main Content */}
          <div className="lg:col-span-3 space-y-8">
            {/* Vitals Section */}
            <div className="card-modern p-6">
              <div className="flex items-center gap-3 mb-6">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{
                    background: "linear-gradient(to right, #3b82f6, #2563eb)", // Tailwind: from-blue-500 to-blue-600
                  }}
                >
                  <Heart
                    className="w-5 h-5"
                    stroke="currentColor"
                    style={{ color: "#ffffff" }}
                  />
                </div>

                <h2 className="text-xl font-bold text-gray-800">
                  Patient Vitals
                </h2>
              </div>

<div className="vitals-grid">
  <div className="vital-card">
    <div className="flex items-center justify-between mb-3">
      <span className="text-sm font-medium text-gray-600">Blood Group</span>
      <Droplets className="w-5 h-5 text-red-600" />
    </div>
    <div className="text-lg font-bold text-gray-800">
      {selectedPatient?.bloodGroup || "Not recorded"}
    </div>
  </div>

  <div className="vital-card">
    <div className="flex items-center justify-between mb-3">
      <span className="text-sm font-medium text-gray-600">Blood Pressure</span>
      <Heart className="w-5 h-5 text-blue-600" />
    </div>
    <div className="text-lg font-bold text-gray-800">
      {selectedPatient?.bloodPressure || "Not recorded"}
    </div>
  </div>

  <div className="vital-card">
    <div className="flex items-center justify-between mb-3">
      <span className="text-sm font-medium text-gray-600">Weight</span>
      <Weight className="w-5 h-5 text-green-600" />
    </div>
    <div className="text-lg font-bold text-gray-800">
      {selectedPatient?.weight ? `${selectedPatient.weight} Kg` : "Not recorded"}
    </div>
  </div>

  <div className="vital-card">
    <div className="flex items-center justify-between mb-3">
      <span className="text-sm font-medium text-gray-600">Height</span>
      <Ruler className="w-5 h-5 text-purple-600" />
    </div>
    <div className="text-lg font-bold text-gray-800">
      {selectedPatient?.height ? `${selectedPatient.height} cm` : "Not recorded"}
    </div>
  </div>

  <div className="vital-card">
    <div className="flex items-center justify-between mb-3">
      <span className="text-sm font-medium text-gray-600">Pulse</span>
      <Heart className="w-5 h-5 text-pink-600" />
    </div>
    <div className="text-lg font-bold text-gray-800">
      {selectedPatient?.pulse ? `${selectedPatient.pulse} bpm` : "Not recorded"}
    </div>
  </div>

  <div className="vital-card">
    <div className="flex items-center justify-between mb-3">
      <span className="text-sm font-medium text-gray-600">Temperature</span>
      <Thermometer className="w-5 h-5 text-orange-600" />
    </div>
    <div className="text-lg font-bold text-gray-800">
      {selectedPatient?.temperature ? `${selectedPatient.temperature} °C` : "Not recorded"}
    </div>
  </div>
</div>
            </div>

            {/* Observation Section */}
            {selectedPatient ? (<div className="card-modern p-6">
              <div className="flex items-center gap-3 mb-6">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{
                    background: "linear-gradient(to right, #22c55e, #16a34a)", // from-green-500 to-green-600
                  }}
                >
                  <ClipboardList
                    className="w-5 h-5"
                    style={{ color: "#ffffff" }}
                  />
                </div>
                <h2 className="text-xl font-bold text-gray-800">
                  Test Observations
                </h2>
                <button
  type="button"
  onClick={handleSaveTest}
  className="gradient-button text-sm flex items-center gap-2 px-4 py-2 rounded ml-auto min-w-[120px]"
  disabled={saving}
>
  {saving ? (

     "Saving..."
  ) : (
    "Save Report"
  )}
</button>
              </div>

              {/* Test Selection */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Select Test
                </label>
                <select
                  className="test-selector text-sm"
                  value={selectedTest?.testName || ""}
                  onChange={handleTestChange}
                >
                  <option value="">--Select Test--</option>
                  {selectedPatient?.testRecommendations.map((test, index) => (
                    <option key={index} value={index}>
                      {test.testName}
                    </option>
                  ))}
                </select>
              </div>

             
<div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Test Name
                    </label>
                    <div className="p-2 bg-gray-50 rounded-lg border h-10 flex items-center">
                      <span className="text-sm text-gray-800">
                        {formik.values.uploadfiles[selectedTest]?.testName}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Priority
                    </label>
                    <div className="p-2 bg-gray-50 rounded-lg border h-10 flex items-center">
                      <span
                        className={`priority-badge ${
                          selectedPatient?.testRecommendations[selectedTest]
                            ?.priority
                            ? `priority-${selectedPatient?.testRecommendations[
                                selectedTest
                              ]?.priority.toLowerCase()}`
                            : ""
                        }`}
                      >
                        {
                          selectedPatient?.testRecommendations[selectedTest]
                            ?.priority
                        }
                      </span>
                    </div>
                  </div>
                </div>

                  {/* Report Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Report Date
                      </label>
                      <DatePicker
  name={`uploadfiles[${selectedTest}].reportDate`}
  value={
    formik.values.uploadfiles[selectedTest]?.reportDate
      ? new Date(formik.values.uploadfiles[selectedTest].reportDate)
      : null
  }
  onChange={(value) =>
    formik.setFieldValue(
      `uploadfiles[${selectedTest}].reportDate`,
      value ? value.toLocaleDateString().split("T")[0] : ""
    )
  }
  onBlur={() =>
    formik.setFieldTouched(`uploadfiles[${selectedTest}].reportDate`, true)
  }
  format="dd-MMM-yyyy"
  placeholder="dd-mmm-yyyy"
  oneTap
  style={{ width: "100%" }}
  className="custom-datepicker"
/>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Report Type
                      </label>
                      <select
                        className="w-full p-2 text-sm border-2 border-gray-200 rounded-lg focus:border-green-500 focus:outline-none transition-colors"
                        value={otherReports ? "Other" : formik.values.uploadfiles[selectedTest]?.reportType}
                        name={`formik.values.uploadfiles[${selectedTest}].reportType`}
                        onChange={(e)=>{
                          if(e.target.value === "Other"){
                            setOtherReport(true);
                            formik.setFieldValue(`uploadfiles[${selectedTest}].reportType`, e.target.value);
                          }else{
                            setOtherReport(false);
                            formik.setFieldValue(`uploadfiles[${selectedTest}].reportType`, e.target.value);
                          }
                          
                        }}
                      >
                        <option value="">Select Report Type</option>
                        <option value="Blood Test">Blood Test</option>
                        <option value="X-Ray">X-Ray</option>
                        <option value="MRI">MRI</option>
                        <option value="CT Scan">CT Scan</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    {otherReports &&<div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Other Report Type
                      </label>
                      <input className="w-full p-2 text-sm border-2 border-gray-200 rounded-lg focus:border-green-500 focus:outline-none transition-colors" type="text"
                      value={formik.values.uploadfiles[selectedTest]?.reportType}
                      name={`formik.values.uploadfiles[${selectedTest}].reportType`}
                      onChange={(e)=>{
                          formik.setFieldValue(`uploadfiles[${selectedTest}].reportType`, e.target.value);
                        }}
                      />
                      {/* <select
                        className="w-full p-2 text-sm border-2 border-gray-200 rounded-lg focus:border-green-500 focus:outline-none transition-colors"
                        value={formik.values.uploadfiles[selectedTest]?.reportType}
                        name={`formik.values.uploadfiles[${selectedTest}].reportType`}
                        onChange={(e)=>{
                          if(e.target.value === "Other"){
                            setOtherReport(true);
                          }
                          formik.setFieldValue(`uploadfiles[${selectedTest}].reportType`, e.target.value);
                        }}
                      >
                        <option value="">Select Report Type</option>
                        <option value="Blood Test">Blood Test</option>
                        <option value="X-Ray">X-Ray</option>
                        <option value="MRI">MRI</option>
                        <option value="CT Scan">CT Scan</option>
                        <option value="Other">Other</option>
                      </select> */}
                    </div>}
                  </div>

                 <div className="mb-8">
  <label className="block text-sm font-medium text-gray-700 mb-3">
    Observation Notes
  </label>
  {/* Toolbar code remains unchanged */}
  <div className="flex flex-wrap gap-2 mb-4 p-2">
    {toolbarButtons.map(({ cmd, label }) => (
      <button
        key={cmd}
        type="button"
        className={`px-3 py-2 text-sm rounded-md border transition-all duration-200 ${
          activeCommands[cmd]
            ? "bg-green-500 text-white border-green-500"
            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
        }`}
        onClick={() => formatText(cmd)}
      >
        {label}
      </button>
    ))}
  </div>

  {/* Fixed ContentEditable */}
 <div
ref={editorRef}
  dir="ltr"
  contentEditable
  className="rich-editor text-sm rounded-t-none relative min-h-[200px] border p-2 bg-white"
  onInput={(e) => {
    formik.setFieldValue(
      `uploadfiles[${selectedTest}].observationNote`,
      e.currentTarget.innerHTML
    );
  }}
  suppressContentEditableWarning={true}
/>

</div>

                  {/* File Upload Section */}
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                      <label className="block text-lg font-bold text-gray-800">
                        Report Files
                      </label>
                      <button
                        type="button"
                        onClick={addReportFile}
                        className="gradient-dark-button text-sm flex items-center gap-2"
                      >
                        <Upload className="w-4 h-4" />
                        Add File
                      </button>
                    </div>

                    {formik.values.uploadfiles[selectedTest]?.imageupload.length === 0 ? (
                      <div className="file-upload-area" onClick={addReportFile}>
                        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                        <p className="text-base text-gray-600 font-medium">
                          Click to add report files
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          Support for PDF, Images, and Word documents
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {formik.values.uploadfiles[selectedTest]?.imageupload.map((file, index) => (
                          <div key={file.id} className="file-item">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Report Title
                                </label>
                                <input
                                  type="text"
                                  className="w-full p-2 text-sm border border-gray-200 rounded-lg focus:border-green-500 focus:outline-none transition-colors"
                                  placeholder="Enter report title"
                                  name={`uploadfiles[${selectedTest}].imageupload[${index}].reportName`}
                                  value={formik.values.uploadfiles[selectedTest]?.imageupload[index].reportName}
                                  onChange={(e)=>{
                                    formik.setFieldValue(`uploadfiles[${selectedTest}].imageupload[${index}].reportName`, e.target.value);
                                  }}
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Upload File
                                </label>
                                <FilePreviewSwitcher fileUrl={file.fileUrl}
                                fileType={file.fileType}
                                setPreviewFile={setPreviewFile}
  fileName={file.fileName}
  onFileChange={(e) => handleFileChange(e, index)} />
                                {/* <div className="relative">
                                  <input
                                    type="file"
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                    onChange={(e) =>
                                      handleFileChange(e, index)
                                    }
                                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                  />
                                  <div className="p-2 border border-gray-200 rounded-lg bg-gray-50 text-center">
                                    {file.formFile ? (
                                      <span className="text-sm text-gray-700">
                                        {file.formFile.name}
                                      </span>
                                    ) : (
                                      <span className="text-sm text-gray-500">
                                        Choose file...
                                      </span>
                                    )}
                                  </div>
                                </div> */}
                              </div>
                            </div>

                            {/* {file.fileUrl && (
                              <div className="flex items-center gap-3 mt-4 pt-4 border-t">
                                {file.fileType?.startsWith("image/") && (
                                  <button
                                    onClick={() => togglePreview(file.id)}
                                    className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                                  >
                                    <Image className="w-4 h-4" />
                                    <span>Preview</span>
                                  </button>
                                )}
                                {file.fileType === "application/pdf" && (
                                  <button
                                    onClick={() => togglePreview(file.id)}
                                    className="flex items-center gap-2 px-3 py-2 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                                  >
                                    <FileText className="w-4 h-4" />
                                    <span>Preview PDF</span>
                                  </button>
                                )}
                                <button
                                  onClick={() => removeReportFile(file.id)}
                                  className="flex items-center gap-2 px-3 py-2 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors ml-auto"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  <span>Remove</span>
                                </button>
                              </div>
                            )} */}

                            {/* File Preview Modal */}
                            {/* {file.showPreview && file.fileUrl && (
                              <div
                                className="modal-overlay"
                                onClick={() => togglePreview(file.id)}
                              >
                                <div
                                  className="modal-content"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {file.fileType?.startsWith("image/") ? (
                                    <img src={file.fileUrl} alt="Preview" />
                                  ) : file.fileType === "application/pdf" ? (
                                    <embed
                                      src={file.fileUrl}
                                      type="application/pdf"
                                      width="800"
                                      height="600"
                                    />
                                  ) : null}
                                </div>
                              </div>
                            )} */}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
            </div>) : <div className="flex justify-center items-center h-96 text-gray-500 text-lg font-semibold">
        Please select a patient to begin entering a report.
      </div>}

            {/* Action Buttons */}
            <div className="flex gap-4">
              <button
              type="button"
                onClick={()=> navigate('/PatientReports')}
                className="px-3 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all duration-300"
                style={{
                  width: "7rem",
                  height: "3rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                Cancel
              </button>
              <button
                className="gradient-button"
                type="submit"
                style={{
                  width: "7rem",
                  height: "3rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                Submit
              </button>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <style>{`
        .patient-dropdown {
          position: relative;
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          transition: all 0.3s ease;
        }
        
        .patient-dropdown:focus-within {
          border-color: #10b981;
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
        }
        
        .patient-dropdown.open {
          border-color: #10b981;
          border-bottom-left-radius: 4px;
          border-bottom-right-radius: 4px;
        }
        
        .dropdown-list {
          position: absolute;
          top: 100%;
          left: -2px;
          right: -2px;
          background: white;
          border: 2px solid #10b981;
          border-top: none;
          border-radius: 0 0 12px 12px;
          max-height: 280px;
          overflow-y: auto;
          z-index: 1000;
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        }
        
        .dropdown-search {
          position: sticky;
          top: 0;
          background: white;
          padding: 12px;
          border-bottom: 1px solid #f3f4f6;
        }
        
        .patient-option {
          padding: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
          border-bottom: 1px solid #f9fafb;
        }
        
        .patient-option:hover {
          background: linear-gradient(90deg, #f0fdf4 0%, #ecfdf5 100%);
          transform: translateX(2px);
        }
        
        .patient-option:last-child {
          border-bottom: none;
          border-radius: 0 0 10px 10px;
        }
        
        .patient-avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 600;
          font-size: 14px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        }
        
        .allergy-tag {
          display: inline-block;
          background: #fee2e2;
          color: #dc2626;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
          margin-right: 4px;
          margin-bottom: 2px;
        }
        
        .condition-tag {
          display: inline-block;
          background: #dbeafe;
          color: #1d4ed8;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
          margin-right: 4px;
          margin-bottom: 2px;
        }
        
        .card-modern {
          background: white;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          border: 1px solid rgba(229, 231, 235, 0.6);
          transition: all 0.3s ease;
        }
        
        .card-modern:hover {
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
          transform: translateY(-2px);
        }
        
        .info-label {
          font-size: 11px;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 4px;
        }
        
        .info-value {
          font-weight: 600;
          color: #1f2937;
          font-size: 14px;
        }
      `}</style>

            {/* Patient Selection Card */}
            <div
            className="patient-option"
  style={{
    background: "white",
    borderRadius: "20px",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    border: "1px solid #f3f4f6",
    padding: "20px",
    marginBottom: "24px",
    maxHeight: "400px",
  }}
>
  {/* Header */}
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "12px",
      marginBottom: "16px",
    }}
  >
    <div
      style={{
        width: "36px",
        height: "36px",
        background: "linear-gradient(135deg, #034939ff, #37997cff, #3a7358ff)",
        borderRadius: "10px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <User style={{ width: "18px", height: "18px", color: "white" }} />
    </div>
    <h3
      style={{
        fontSize: "16px",
        fontWeight: "600",
        color: "#1f2937",
      }}
    >
      Patient Information
    </h3>
  </div>

  {/* Patient Search Input + Dropdown */}
  <div ref={dropdownRef} className="relative mb-2">
    <div className="relative w-full">
      <input
        type="text"
        placeholder="Search patient by name"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        onFocus={() => setIsDropdownOpen(true)}
        className="w-full px-3 py-2.5 pr-9 bg-gray-50 border border-gray-200 rounded-lg 
                   text-sm outline-none transition-all duration-200
                   focus:border-slate-600 focus:bg-white focus:ring-2 focus:ring-slate-100"
      />
      <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
        <Search className="w-4 h-4 text-gray-400" />
      </div>
    </div>

    {isDropdownOpen && (
      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-44 overflow-y-auto">
        {filteredPatients.length > 0 ? (
          filteredPatients.map((patient) => (
            <div
              key={patient.id}
              onClick={() => handlePatientSelect(patient)}
              className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
            >
              <div className="font-medium text-sm text-gray-800 mb-0.5">
                {patient.fullName}
              </div>
              <div className="text-xs text-gray-500">
                {patient.age} • {patient.gender} • Phone: {patient.contactNumber}
              </div>
            </div>
          ))
        ) : (
          <div className="p-3 text-center text-sm text-gray-500">
            No patients found matching "{searchTerm}"
          </div>
        )}
      </div>
    )}
  </div>

  {/* Selected Patient */}
  {selectedPatient ? (
    <div
      style={{
        border: "1.5px solid #3A6073",
        borderRadius: "10px",
        padding: "12px",
        background: "linear-gradient(135deg, #f0fdf8 0%, #e8faf5 100%)",
        maxHeight: "200px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "10px",
        }}
      >
        <div style={{ flex: 1 }}>
          <h4
            style={{
              fontSize: "15px",
              fontWeight: "600",
              color: "#1f2937",
              marginBottom: "3px",
              lineHeight: "1.3",
            }}
          >
            {selectedPatient.fullName}
          </h4>
          <div
            style={{
              fontSize: "12px",
              color: "#6b7280",
              lineHeight: "1.4",
            }}
          >
            Age:{selectedPatient.age} • {selectedPatient.gender} • Phone:{" "}
            {selectedPatient.contactNumber} • AppointmentID:{selectedPatient.appointmentID}
          </div>
        </div>

        {/* Clear Button */}
        <button
          onClick={() => setSelectedPatient(null)}
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: "#9ca3af",
            padding: "2px",
            borderRadius: "4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onMouseEnter={(e) => {
            e.target.style.background = "rgba(239, 68, 68, 0.1)";
            e.target.style.color = "#ef4444";
          }}
          onMouseLeave={(e) => {
            e.target.style.background = "transparent";
            e.target.style.color = "#9ca3af";
          }}
        >
          <X style={{ width: "14px", height: "14px" }} />
        </button>
      </div>

      <div style={{ display: "grid", gap: "8px" }}>
        <div>
          <span
            style={{
              fontSize: "11px",
              fontWeight: "600",
              color: "#374151",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            Allergies:
          </span>
          <span
            style={{
              fontSize: "12px",
              color: "#6b7280",
              marginLeft: "6px",
            }}
          >
            {selectedPatient.allergies_ifany &&
            selectedPatient.allergies_ifany !== "None"
              ? selectedPatient.allergies_ifany
              : "None"}
          </span>
        </div>

        <div>
          <span
            style={{
              fontSize: "11px",
              fontWeight: "600",
              color: "#374151",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            Conditions:
          </span>
          <span
            style={{
              fontSize: "12px",
              color: "#6b7280",
              marginLeft: "6px",
            }}
          >
            {selectedPatient.existingMedicalConditions &&
            selectedPatient.existingMedicalConditions !== "None"
              ? selectedPatient.existingMedicalConditions
              : "None"}
          </span>
        </div>
      </div>

      <button
        type="button"
        onClick={() =>
          navigate(`/ClinicPatientView/${selectedPatient?.userUniqueID}?from=${encodeURIComponent(
              location.pathname + location.search
            )}`)}
        style={{
          color: "#3A6073",
          background: "transparent",
          border: "none",
          fontSize: "12px",
          fontWeight: "500",
          cursor: "pointer",
          textDecoration: "underline",
          padding: "0",
          marginTop: "10px",
          transition: "all 0.2s",
        }}
        onMouseEnter={(e) => {
          e.target.style.color = "#2d4a59";
        }}
        onMouseLeave={(e) => {
          e.target.style.color = "#3A6073";
        }}
      >
        View patient details →
      </button>
    </div>
  ) : (
    // Empty State
    <div
      style={{
        textAlign: "center",
        padding: "20px 12px",
        color: "#9ca3af",
      }}
    >
      <div
        style={{
          width: "40px",
          height: "40px",
          background: "#f3f4f6",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 8px",
        }}
      >
        <User style={{ width: "20px", height: "20px" }} />
      </div>
      <p
        style={{
          fontSize: "13px",
          marginBottom: "2px",
          fontWeight: "500",
        }}
      >
        No patient selected
      </p>
      <p style={{ fontSize: "11px", color: "#9ca3af" }}>
        Click above to search and select
      </p>
    </div>
  )}
</div>


            {/* Doctor Information Card */}
            <div className="card-modern p-6">
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{
                    background: "linear-gradient(to right, #6366F1, #4F46E5)",
                  }} // indigo-500 to indigo-600
                >
                  <UserCheck className="w-5 h-5" style={{ color: "#ffffff" }} />
                </div>
                <h3 className="font-bold" style={{ color: "#1F2937" }}>
                  Doctor Information
                </h3>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="info-label">Doctor Name</label>
                  <p className="info-value">{selectedPatient?.doctorName}</p>
                </div>
                <div>
                  <label className="info-label">Specialization</label>
                  <p className="info-value">{selectedPatient?.department}</p>
                </div>
                
              </div>
            </div>
          </div>
        </div>
      </div>
                  {previewFile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white w-full max-w-4xl mx-4 rounded-xl shadow-lg relative">
            <button
              onClick={closePreview}
              className="absolute top-4 right-4 text-gray-500 hover:text-red-600 text-2xl"
            >
              &times;
            </button>

            <div className="p-6">
              <h2 className="text-lg font-semibold mb-4 text-gray-800">{previewFile.fileName}</h2>
              <div className="border rounded overflow-hidden max-h-[75vh]">
                {type?.type === "image" ? (
                  <img
                    src={previewFile.fileUrl}
                    alt={previewFile.fileName}
                    className="w-full object-contain max-h-[70vh] mx-auto"
                  />
                ) : type?.type === "pdf" ? (
                  <iframe
                    src={previewFile.fileUrl}
                    title={previewFile.fileName}
                    className="w-full h-[70vh]"
                  />
                ) : (
                  <p className="text-gray-500 p-4 text-center">Preview not supported</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </form>
  }

  return (content);
};

export default AddPatientReports;
